import React, { useLayoutEffect, useRef } from 'react'
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined'
import WarningOutlined from '@ant-design/icons/WarningOutlined'
import useFeatureFlags, {
  type TopBannerAlertFlag,
} from '../../hooks/useFeatureFlags'
import './TopBannerAlert.css'

const FLAG_KEY = 'platform.top-banner-alert'
const BODY_CLASS = 'has-top-banner'

const TYPES = new Set(['info', 'warn'])

function toType(value: string | undefined): 'info' | 'warn' {
  return TYPES.has(value ?? '') ? (value as 'info' | 'warn') : 'info'
}

function isFlagEnabled(flag: TopBannerAlertFlag | undefined): boolean {
  return Boolean(flag?.enabled && flag.message)
}

export const TopBannerAlert = () => {
  const { flags } = useFeatureFlags()
  const bannerRef = useRef<HTMLDivElement>(null)
  const flag = flags[FLAG_KEY] as TopBannerAlertFlag | undefined
  const active = isFlagEnabled(flag)

  useLayoutEffect(() => {
    const root = document.documentElement
    const body = document.body

    if (!active) {
      body.classList.remove(BODY_CLASS)
      root.style.removeProperty('--top-banner-height')
      const sidebarReset = document.querySelector<HTMLElement>(
        '#app-layout-node > div > div:first-child'
      )
      if (sidebarReset) sidebarReset.style.removeProperty('top')
      return undefined
    }

    body.classList.add(BODY_CLASS)

    const measure = () => {
      const height = bannerRef.current?.getBoundingClientRect().height ?? 0
      root.style.setProperty('--top-banner-height', `${height}px`)
      const sidebar = document.querySelector<HTMLElement>(
        '#app-layout-node > div > div:first-child'
      )
      if (sidebar) sidebar.style.top = `${height}px`
    }

    measure()
    const frame = requestAnimationFrame(measure)
    const observer = new ResizeObserver(measure)
    if (bannerRef.current) {
      observer.observe(bannerRef.current)
    }

    return () => {
      body.classList.remove(BODY_CLASS)
      root.style.removeProperty('--top-banner-height')
      const sidebarCleanup = document.querySelector<HTMLElement>(
        '#app-layout-node > div > div:first-child'
      )
      if (sidebarCleanup) sidebarCleanup.style.removeProperty('top')
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [active, flag?.message, flag?.ctaLabel])

  if (!active || !flag) {
    return null
  }

  const type = toType(flag.type)
  const Icon = type === 'warn' ? WarningOutlined : InfoCircleOutlined

  return (
    <div
      ref={bannerRef}
      className={`top-banner-alert top-banner-alert--${type}`}
      role={type === 'warn' ? 'alert' : 'status'}
    >
      <div className="top-banner-alert__content">
        <Icon className="top-banner-alert__icon" />
        <span className="top-banner-alert__body">
          {flag.message}
          {flag.ctaUrl && flag.ctaLabel ? (
            <>
              {' '}
              <a
                className="top-banner-alert__cta"
                href={flag.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {flag.ctaLabel}
              </a>
            </>
          ) : null}
        </span>
      </div>
    </div>
  )
}

export default TopBannerAlert
